// Remover importações de ../types
// import { 
//   Game as LibGame, 
//   IRecommender, 
//   RecommendationFilters 
// } from '../types';
// Importar tudo de ../../types/index
import { 
  Game as IndexGame, 
  RecommendationFilters, 
  // IRecommender // Não importar mais, definir localmente
} from '../../types/index';
import { DatabaseUtils } from './utils/databaseUtils';
import { GameProcessingUtils } from './utils/gameProcessingUtils';
import { RecommendationDataService } from './services/recommendationDataService';

// Definir IRecommender localmente usando tipos centrais
interface IRecommender {
  getRecommendations(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: RecommendationFilters
  ): Promise<IndexGame[]>;
  // Adicionar outros métodos se IRecommender tiver mais (ex: getFallback?)
}

/**
 * Classe base para todos os recommenders
 * Agora usa apenas tipos de ../../types/index
 */
// Usar a interface local
export abstract class BaseRecommender implements IRecommender { 
  /**
   * Método principal - já retorna IndexGame[]
   */
  public abstract getRecommendations(
    userId: string,
    count: number,
    excludeGameIds: string[],
    filters?: RecommendationFilters
  ): Promise<IndexGame[]>;

  /**
   * Busca recomendações frescas - já retorna IndexGame[]
   */
  protected async getFreshRecommendations(count: number, excludeIds: string[] = []): Promise<IndexGame[]> {
    try {
      // Usar o serviço de dados centralizado para obter jogos populares
      const highRatedGames = await RecommendationDataService.getPopularGames(count * 2, excludeIds);
      
      if (highRatedGames.length >= count) {
        return GameProcessingUtils.shuffleArray(highRatedGames).slice(0, count);
      }
      
      // Se não encontrar jogos suficientes, buscar jogos recentes
      const recentGames = await RecommendationDataService.getRecentGames(count * 2, excludeIds);
        
      // Combinar os resultados (sem duplicar)
      const allGames = [...highRatedGames];
      const highRatedIds = new Set(highRatedGames.map(g => g.id));
      
      // Adicionar jogos recentes que não estão nos bem avaliados
      recentGames.forEach(game => {
        if (!highRatedIds.has(game.id)) {
          allGames.push(game);
        }
      });
      
      return GameProcessingUtils.shuffleArray(allGames).slice(0, count);
    } catch (error) {
      console.error('Erro ao buscar jogos recentes:', error);
      
      // Último recurso: buscar qualquer jogo
      try {
        const games = await DatabaseUtils.executeSafeQuery({}, count);
        return games;
      } catch (fallbackError) {
        console.error('Falha no método de fallback para obter jogos:', fallbackError);
        return [];
      }
    }
  }

  /**
   * Obtém jogos bem avaliados - já retorna IndexGame[]
   * A lógica de minRating agora é controlada por DatabaseUtils/RecommendationDataService usando RECOMMENDATION_CONFIG.
   */
  protected async getUserFavoriteGames(userId: string): Promise<IndexGame[]> {
    try {
      // RecommendationDataService.getUserPreferences já busca os jogos favoritos
      // usando DatabaseUtils.getUserFavoriteGames, que por sua vez usa
      // RECOMMENDATION_CONFIG.recommenderThresholds.minRatingForUserFavorite.
      // Se a intenção aqui é apenas obter essa lista de jogos, podemos simplificar
      // ou chamar diretamente o DatabaseUtils se uma lógica de fallback diferente for necessária.
      
      // Chamada direta para DatabaseUtils para obter a lista baseada na config central.
      let userFavorites = await DatabaseUtils.getUserFavoriteGames(userId);
      
      // Se ainda não temos jogos (mesmo com o minRating da config), usar a coleção como fallback.
      // A lógica de tentar um rating menor (ex: 3) que existia aqui antes é agora
      // mais implicitamente tratada pelo valor de minRatingForUserFavorite na config (3.5).
      // Se for necessário um fallback explícito para um rating AINDA MENOR, teria que ser adicionado aqui
      // chamando _fetchUserGamesInternal com um criteria.minRating customizado.
      if (!userFavorites || userFavorites.length === 0) {
        console.log(`[BaseRecommender] Nenhum jogo favorito encontrado para ${userId} com config.minRatingForUserFavorite. Buscando coleção completa como fallback.`);
        userFavorites = await DatabaseUtils.getUserCollectionGames(userId);
      }
      
      return userFavorites || [];
    } catch (error) {
      console.error('[BaseRecommender] Erro ao obter jogos favoritos do usuário:', error);
      return [];
    }
  }
  
  /**
   * Implementação de fallback - já retorna IndexGame[]
   */
  protected async getFallbackRecommendations(count: number, excludeGameIds: string[]): Promise<IndexGame[]> {
    try {
      const validExcludeIds = this.validateExcludeIds(excludeGameIds);
      return this.getFreshRecommendations(count, validExcludeIds);
    } catch (error) {
      console.error('Erro fatal no sistema de recomendação:', error);
      return [];
    }
  }

  /**
   * Valida IDs de exclusão e remove inválidos
   */
  protected validateExcludeIds(excludeGameIds: string[]): string[] {
    if (!Array.isArray(excludeGameIds)) {
      console.warn('Lista de IDs de exclusão não é um array válido, ajustando para array vazio');
      return [];
    }
    
    // Filtrar IDs inválidos
    const validIds = excludeGameIds.filter(id => id && typeof id === 'string');
    
    // Se houve filtragem, registrar
    if (validIds.length !== excludeGameIds.length) {
      console.warn(`Filtrados ${excludeGameIds.length - validIds.length} IDs inválidos da lista de exclusão`);
    }
    
    return validIds;
  }
  
  /**
   * Aplica filtros padrão - RecommendationFilters já vem de ../../types/index
   */
  protected async applyDefaultFilters(
    userId: string,
    filters: RecommendationFilters = {}
  ): Promise<RecommendationFilters> {
    try {
      // Usar o serviço centralizado para aplicar filtros inteligentes
      return await RecommendationDataService.applySmartFilters(userId, filters);
    } catch (error) {
      console.error('Erro ao aplicar filtros padrão:', error);
      return filters;
    }
  }
  
  /**
   * Obtém IDs de jogos que devem ser excluídos das recomendações
   * Inclui jogos que o usuário já possui e não gostou
   * A exclusão global agora é feita diretamente nas queries via DatabaseUtils
   */
  protected async getCompleteExcludeIds(
    userId: string, 
    baseExcludeIds: string[] = [],
    includeOwned: boolean = true
  ): Promise<string[]> {
    try {
      // Log dos IDs recebidos
      console.log(`[DETAILED DEBUG] getCompleteExcludeIds - Recebidos baseExcludeIds:`, baseExcludeIds, `e userId: ${userId}`);

      // Obter IDs centralizados (possuídos, não gostados, etc.)
      const systemExcludeIds = await RecommendationDataService.getExcludeIds(
        userId, 
        includeOwned, 
        false // não incluir jogos curtidos
      );
      
      // Log dos IDs do sistema
      console.log(`[DETAILED DEBUG] getCompleteExcludeIds - Obtidos systemExcludeIds:`, systemExcludeIds);

      // === REMOVER LÓGICA DE BUSCA DE EXCLUSÕES GLOBAIS ===
      // const globalExcludeGameIds = new Set<string>();
      // const globalExclusions = RECOMMENDATION_CONFIG.globalExclusions;
      // ... (código de busca usando DatabaseUtils.executeSafeQuery removido) ...
      // === FIM DA REMOÇÃO ===

      // Combinar APENAS com os IDs base e do sistema
      const allIds = [...baseExcludeIds, ...systemExcludeIds];
      
      // Remover duplicatas
      const finalExcludeIds = Array.from(new Set(allIds));
      console.log(`[DETAILED DEBUG] getCompleteExcludeIds - Final exclude IDs (${finalExcludeIds.length}):`, finalExcludeIds);
      return finalExcludeIds;
      
    } catch (error) {
      console.error('Erro ao obter lista completa de exclusões:', error);
      return this.validateExcludeIds(baseExcludeIds);
    }
  }
} 